import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers/AuthUser';
import { UserType } from '../../types';
import { Conv, ApiConv } from '../../models/Conv';
import Spinner from '../../components/Spinner';

type MessageAsideProps = {
  activeConv?: Conv;
  selectConv: (conv?: Conv) => void;
  setIsNoConv: (isNoConv: boolean) => void;
  refreshTrigger?: number;
}

const MessageAside = ({ activeConv, selectConv, setIsNoConv, refreshTrigger }: MessageAsideProps) => {
  const [ convs, setConvs ] = useState<Conv[]>();
  const [ unreadConvIds, setUnreadConvIds ] = useState<number[]>();
  const [ includeHidden, setIncludeHidden ] = useState<boolean>(false);
  const { get, loading } = useApi();
  const { user } = useAuthUserContext();

  const isDonee = user?.userType === UserType.Donee;

  const fetchConvs = useCallback(() => {
    get({
      endpoint: `/api/v1/convs`,
      params: { include_hidden: includeHidden ? "true" : "false" },
      onSuccess: (res) => {
        const apiConvs: ApiConv[] = res.data.convs;
        setConvs(apiConvs.map(c => new Conv(c, isDonee)));
        setUnreadConvIds(res.data.unread_convs);
        if (apiConvs.length === 0) {
          setIsNoConv(true);
        } else {
          setIsNoConv(false);
          selectConv(new Conv(apiConvs[0], isDonee));
        }
      },
      onError: () => {
        toast.error("会話メッセージの取得に失敗しました。時間をおいて再度お試しください。");
      },
    });
  }, [ get, includeHidden, selectConv, setIsNoConv, isDonee ]);

  useEffect(() => {
    fetchConvs();
  }, [ fetchConvs ])

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchConvs();
    }
  }, [ refreshTrigger, fetchConvs ])

  const convsPc = convs?.map(c => {
    return (
      <li
        key={ c.id }
        className={ activeConv?.id === c.id ? "active" : "" }
        onClick={ () => selectConv(c) }
      >
        { isDonee ? c.donor.name : c.donee.name }（No.{ isDonee ? c.donor.userNo : c.donee.userNo }）
        { c.hidden && <span>非表示</span> }
        { unreadConvIds?.includes(c.id) &&
          <span>NEW</span>
        }
      </li>
    )
  })

  const convsSm = convs?.map(c => {
    return (
      <option key={ c.id } value={ c.id }>
        { isDonee ? c.donor.name : c.donee.name }（No.{ isDonee ? c.donor.userNo : c.donee.userNo }）
        { c.hidden && " [非表示]" }
        { unreadConvIds?.includes(c.id) &&
          " NEW"
        }
      </option>
    )
  })

  const handleConvChange = (newConvId: number) => {
    selectConv(convs?.find(c => c.id === newConvId));
  }

  return (
    <aside>
      <div className="form">
        <span className="wpcf7-form-control-wrap checkbox-content">
          <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
            <span className="wpcf7-list-item">
              <label>
                <input
                  type="checkbox"
                  name="checkbox-content[]"
                  value="非表示の会話も表示する"
                  checked={ includeHidden }
                  onChange={ (e) => setIncludeHidden(e.target.checked) }
                />
                <span className="wpcf7-list-item-label">非表示の会話も表示する</span>
              </label>
            </span>
          </span>
        </span>
      </div>
      <ul className="pconly">
        { loading ? <Spinner centered={ true } color={ isDonee ? "pink" : "blue" } /> : convsPc }
      </ul>
      <div className="form smonly">
        <div className="select select01">
          <select
            value={ activeConv?.id }
            onChange={ e => handleConvChange(Number(e.target.value)) }
          >
            { loading ? (
              <option>読み込み中...</option>
            ) : (
              <>
                <option value="">選択してください</option>
                { convsSm }
              </>
            ) }
          </select>
        </div>
      </div>
    </aside>
  );
}

export default MessageAside;
